'use client';

import { FC, useEffect, useState, memo } from 'react';

import { Document } from '@contentful/rich-text-types';

import classNames from 'classnames';

import { Badge } from '@/components';

import useAuth from '@/context/AuthContext';
import useFeature from '@/hooks/useFeature';
import { TypeInverseTextHeroFields } from '@/core/generated-types';
import { getPaddingClass } from '@/utils/paddingHelpers';

import { RichText } from '../RichText';
import { RichTextHeading } from '../RichTextHeading';
import { UniversalCta } from '../UniversalCta';

import styles from './InverseTextHero.module.scss';

const componentName = 'inverseTextHero';

const InverseTextHero: FC<TypeInverseTextHeroFields> = ({
  leadingText,
  mainText,
  mainRichText,
  cta,
  trailingText,
  backgroundColor,
  badge,
  descriptionTextLoggedOut,
  descriptionTextLoggedIn,
  isFullWidth,
  topPadding = 10,
  bottomPadding = 10,
}) => {
  const isBfcmEnabled = useFeature('money_bfcm-mode-2025');
  const [hydrated, setHydrated] = useState(false);

  const { user } = useAuth();

  const useLoggedInText = !!user;

  const textToDisplay = useLoggedInText
    ? descriptionTextLoggedIn
    : descriptionTextLoggedOut;

  const topPaddingClass = getPaddingClass(topPadding, 'top');
  const bottomPaddingClass = getPaddingClass(bottomPadding, 'bottom');

  // This hydrated state is needed to avoid hydration errors due to SSR https://nextjs.org/docs/messages/react-hydration-error#solution-1-using-useeffect-to-run-on-the-client-only
  useEffect(() => {
    setHydrated(true);
  }, []);

  return (
    <div
      className={classNames(
        `px-6 bg-${backgroundColor?.fields?.color}`,
        topPaddingClass,
        bottomPaddingClass,
        {
          'lg:pt-12': topPadding === 10 && topPaddingClass === 'pt-10', // Apply lg:pt-12 only for default value
          'lg:pb-12': bottomPadding === 10 && bottomPaddingClass === 'pb-10', // Apply lg:pb-12 only for default value
        },
      )}
    >
      <div
        className={classNames('mx-auto text-center', {
          'max-w-[740px]': !isFullWidth,
        })}
      >
        {badge && (
          <div className="mb-3 lg:mb-4">
            <Badge {...badge.fields} />
          </div>
        )}

        {leadingText && (
          <div
            className="mb-2 text-lg font-bold md:text-2xl"
            data-testid={`${componentName}-leadingText`}
          >
            {leadingText}
          </div>
        )}

        {mainText && (
          <div
            className="text-h-x3 font-bold md:text-[42px] md:leading-[56px] [&_span]:font-bradford [&_span]:font-medium"
            data-testid={`${componentName}-mainText`}
            dangerouslySetInnerHTML={{ __html: mainText }}
          />
        )}

        {mainRichText && (
          <RichTextHeading
            classes={classNames({
              'text-h-x2 md:text-h-x1': isFullWidth,
            })}
            boldClass={isBfcmEnabled ? 'text-red-v2' : ''}
            omitSizeString={isFullWidth}
            content={mainRichText as Document}
          />
        )}

        {cta && (
          <UniversalCta
            link={cta}
            testId={`${componentName}-cta`}
            className="mt-6 inline-flex"
          />
        )}

        {textToDisplay && hydrated && (
          <div className={`${styles.inverseTextHero} text-sm mt-4`}>
            <RichText content={textToDisplay as Document} />
          </div>
        )}

        {trailingText && hydrated && (
          <div className="mt-6 text-sm">
            <RichText content={trailingText as Document} />
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(InverseTextHero);
